import type { ReactNode } from 'react';
import { ScrollText } from 'lucide-react';
import { Card, EmptyState, LoadMore, TableSkeleton } from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { useEntityAudit } from '@/hooks/useAudit';
import { formatDateTime } from '@/utils/format';
import { auditActionLabel } from '@/utils/labels';

/**
 * What the trail says about a server (ADR-0058): its connection, its agent,
 * a drain, a credential added or its password shown — who, when, and how it
 * ended. Kept for a year, like the rest of the trail.
 */
export function ServerJournal({ nodeId }: { readonly nodeId: string }): ReactNode {
  const query = useEntityAudit('node', nodeId);

  if (query.isPending) {
    return (
      <Card flush>
        <TableSkeleton rows={5} columns={4} />
      </Card>
    );
  }
  if (query.isError) return <ErrorState error={query.error} fallback="Журнал не загрузился" />;

  const events = query.data.pages.flatMap((page) => page.items);
  return (
    <>
      <Card flush>
        {events.length === 0 ? (
          <EmptyState icon={ScrollText} message="В журнале об этом сервере пока ничего нет" />
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Когда</th>
                  <th scope="col">Что</th>
                  <th scope="col">Кто</th>
                  <th scope="col">Результат</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.id}>
                    <td className="nowrap">{formatDateTime(event.createdAt)}</td>
                    <td title={event.action}>{auditActionLabel(event.action)}</td>
                    <td>{event.actorLabel ?? 'система'}</td>
                    <td>
                      {event.result === 'success' ? (
                        'успешно'
                      ) : (
                        <span className="tone-down">
                          ошибка{event.errorCode ? <span className="mono text-sm"> · {event.errorCode}</span> : null}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <LoadMore shown={events.length} hasMore={query.hasNextPage} busy={query.isFetchingNextPage} onMore={() => void query.fetchNextPage()} />
    </>
  );
}
